import { useState } from "react";
import toast from "react-hot-toast";
import { BookMarked, Loader2, Edit2, FileText, FileUp, ExternalLink, CheckCircle2, XCircle, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    useAdminOutputPublishers,
    useUpdateOutputPublisher,
    useUploadPublisherTemplate,
    useUploadPublisherGuideline,
    type ConferenceOutputPublisherAdmin,
} from "@/services/adminConferenceOutputService";
import { StatCard } from "@/pages/private/admin/conference-output/OutputDecisionsPage";

export default function OutputPublishersPage() {
    const { data: publishers, isLoading } = useAdminOutputPublishers();
    const updatePublisher = useUpdateOutputPublisher();
    const uploadTemplate = useUploadPublisherTemplate();
    const uploadGuideline = useUploadPublisherGuideline();

    const [editTarget, setEditTarget] = useState<ConferenceOutputPublisherAdmin | null>(null);
    const [editForm, setEditForm] = useState({ display_name: "", website_url: "", is_active: true });
    const [uploadingId, setUploadingId] = useState<{ id: number; kind: "template" | "guideline" } | null>(null);

    const openEdit = (p: ConferenceOutputPublisherAdmin) => {
        setEditTarget(p);
        setEditForm({ display_name: p.display_name, website_url: p.website_url || "", is_active: p.is_active });
    };

    const handleSaveEdit = async () => {
        if (!editTarget) return;
        try {
            await updatePublisher.mutateAsync({
                id: editTarget.id,
                payload: { display_name: editForm.display_name, website_url: editForm.website_url || null, is_active: editForm.is_active },
            });
            toast.success("Data publisher berhasil diperbarui");
            setEditTarget(null);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal menyimpan data");
        }
    };

    const handleUploadTemplate = async (id: number, file: File) => {
        setUploadingId({ id, kind: "template" });
        try {
            await uploadTemplate.mutateAsync({ id, file });
            toast.success("Template naskah berhasil diunggah");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal mengunggah template");
        } finally {
            setUploadingId(null);
        }
    };

    const handleUploadGuideline = async (id: number, file: File) => {
        setUploadingId({ id, kind: "guideline" });
        try {
            await uploadGuideline.mutateAsync({ id, file });
            toast.success("Author guideline berhasil diunggah");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal mengunggah guideline");
        } finally {
            setUploadingId(null);
        }
    };

    const activeCount = publishers?.filter((p) => p.is_active).length ?? 0;
    const readyCount = publishers?.filter((p) => p.template_file_path && p.guideline_file_path).length ?? 0;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-5 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none"><BookMarked className="w-64 h-64 text-blue-600" /></div>
                <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm relative z-10"><BookMarked className="w-8 h-8" /></div>
                <div className="relative z-10">
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Publisher Proceeding</h1>
                    <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">Kelola nama tampilan, website, template naskah, dan author guideline per publisher (KNE, SHS, dst) - dipakai dinamis di email & form registrasi penulis.</p>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <StatCard icon={BookMarked} label="Total Publisher" value={publishers?.length} color="blue" />
                <StatCard icon={CheckCircle2} label="Aktif" value={activeCount} color="emerald" />
                <StatCard icon={FileText} label="Dokumen Lengkap" value={readyCount} color="purple" />
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
            ) : (
                <div className="space-y-4">
                    {publishers?.map((p) => (
                        <div key={p.id} className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    <span className="text-xs font-black uppercase tracking-wide px-3 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 shrink-0">{p.code}</span>
                                    <div className="min-w-0">
                                        <p className="font-bold text-slate-900 dark:text-white">{p.display_name}</p>
                                        {p.website_url && (
                                            <a href={p.website_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-0.5">
                                                <Globe className="w-3 h-3" /> {p.website_url} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                    <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md shrink-0 ${p.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
                                        {p.is_active ? "Aktif" : "Nonaktif"}
                                    </span>
                                </div>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(p)} className="h-9 w-9 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent><p>Edit nama & website</p></TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-3 mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                    {p.template_file_path ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <XCircle className="w-4 h-4 text-slate-300 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Template Naskah</p>
                                        {p.template_file_path && <a href={p.template_file_path} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline">Lihat file</a>}
                                    </div>
                                    <label className="shrink-0">
                                        <Button asChild variant="outline" size="sm" className="rounded-lg pointer-events-none">
                                            <span>{uploadingId?.id === p.id && uploadingId.kind === "template" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}</span>
                                        </Button>
                                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => e.target.files?.[0] && handleUploadTemplate(p.id, e.target.files[0])} />
                                    </label>
                                </div>
                                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                    {p.guideline_file_path ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <XCircle className="w-4 h-4 text-slate-300 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Author Guideline</p>
                                        {p.guideline_file_path && <a href={p.guideline_file_path} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline">Lihat file</a>}
                                    </div>
                                    <label className="shrink-0">
                                        <Button asChild variant="outline" size="sm" className="rounded-lg pointer-events-none">
                                            <span>{uploadingId?.id === p.id && uploadingId.kind === "guideline" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}</span>
                                        </Button>
                                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => e.target.files?.[0] && handleUploadGuideline(p.id, e.target.files[0])} />
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
                <DialogContent className="sm:max-w-[440px] bg-white dark:bg-slate-900 rounded-3xl">
                    <DialogHeader>
                        <DialogTitle>Edit Publisher {editTarget?.code}</DialogTitle>
                        <DialogDescription>Nama tampilan dan website ditampilkan dinamis di email & form registrasi penulis.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Nama Tampilan</Label>
                            <Input value={editForm.display_name} onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))} />
                        </div>
                        <div>
                            <Label className="mb-2 block">Website</Label>
                            <Input value={editForm.website_url} onChange={(e) => setEditForm((f) => ({ ...f, website_url: e.target.value }))} placeholder="https://..." />
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <Label>Aktif</Label>
                            <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: v }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)} className="rounded-xl">Batal</Button>
                        <Button onClick={handleSaveEdit} disabled={updatePublisher.isPending} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                            {updatePublisher.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
